import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, NgZone, type OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { type Notification, NOTIFICATION_TYPE } from '@core/models/app.model';
import { NotifyService } from '@core/services/notifyService.service';

@UntilDestroy()
@Component({
  selector: 'ume-notify',
  templateUrl: './notify.component.html',
  styleUrls: ['./notify.component.scss'],
  imports: [MatButtonModule, MatIconModule, CommonModule],
})
export class NotifyComponent implements OnInit {
  readonly notificationType = NOTIFICATION_TYPE;

  private readonly _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly router: Router = inject(Router);
  private readonly _zone: NgZone = inject(NgZone);
  private readonly notifyService: NotifyService = inject(NotifyService);

  public notification: Notification | null = null;

  ngOnInit(): void {
    this.router.events.pipe(untilDestroyed(this)).subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.hide();
        this._cdr.markForCheck();
      }
    });

    this.notifyService.notificationEmitter
      .pipe(untilDestroyed(this))
      .subscribe((notification: Notification) => {
        this.notification = notification;
        const timeout = notification.duration ?? 4000;
        this._cdr.detectChanges();

        if (this.notification.type === NOTIFICATION_TYPE.SUCCESS) {
          this._zone.runOutsideAngular(() => {
            setTimeout(() => {
              this.hide();
              this._cdr.detectChanges();
            }, timeout);
          });
        }

        if (this.notification.type === NOTIFICATION_TYPE.INFO) {
          this._zone.runOutsideAngular(() => {
            setTimeout(() => {
              this.hide();
              this._cdr.detectChanges();
            }, timeout);
          });
        }
      });
  }

  public get notificationTitle(): string {
    if (!this.notification) {
      return '';
    }
    switch (this.notification.type) {
      case NOTIFICATION_TYPE.SUCCESS:
        return 'Success';
      case NOTIFICATION_TYPE.INFO:
        return 'Info';
      case NOTIFICATION_TYPE.CAUTION:
      case NOTIFICATION_TYPE.CAUTION_ONLY:
        return 'Warning';
      case NOTIFICATION_TYPE.ERROR:
      case NOTIFICATION_TYPE.ERROR_ONLY:
        return 'Error';
      default:
        return 'Success';
    }
  }

  public hide(): void {
    this.notification = null;
  }
}
